import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box, Button, Chip, Typography, Drawer, Paper
} from '@mui/material';
import {
  Payment as PaymentIcon,
  AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import { GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchApprovedExpensesGrouped, fetchExpenseCategories } from '../../store/expenseStore/expenseThunk';
import { Utils } from '../../utils/common-utils';
import DialogExpenseSettlementForm from '../../components/Expenses/DialogExpenseSettlementForm';
import ExpenseFilter from '../../components/Expenses/ExpenseFilter';
import { ExpenseFilters } from '../../utils/expense.types';
import { ApiStatus, defaultRowsPerPageOptions } from '../../utils/api.types';
import CustomDataGrid from '../../components/DataGrid/DataGrid';
import moment from 'moment';

const ExpenseSettlement: React.FC = () => {
  const dispatch = useAppDispatch();
  const { categories } = useAppSelector(state => state.expense);

  const [loading, setLoading] = useState(false);
  const [expenses, setExpenses] = useState<any[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [settleDrawerOpen, setSettleDrawerOpen] = useState(false);
  const [settleExpenses, setSettleExpenses] = useState<any[]>([]);
  const [settleParty, setSettleParty] = useState<any>(null);
  const [filters, setFilters] = useState<ExpenseFilters>({});
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result: any = await dispatch(fetchApprovedExpensesGrouped({ page, limit: pageSize })).unwrap();
      setExpenses(result?.expenses || []);
      setRowCount(result?.total || 0);
      setTotalAmount(result?.totalAmount || 0);
    } catch (err) {
      console.error('Error loading approved expenses:', err);
    }
    setLoading(false);
  }, [dispatch, page, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    dispatch(fetchExpenseCategories({ flat: false }));
  }, []);

  const handleFiltersChange = useCallback((newFilters: ExpenseFilters) => {
    setFilters(newFilters);
    setPage(0);
  }, []);

  const filteredExpenses = expenses.filter(e => {
    if (filters.vendor && e.partyId !== filters.vendor) return false;
    if (filters.employee && e.partyId !== filters.employee) return false;
    if (filters.expenseCategory && e.expenseCategory?._id !== filters.expenseCategory) return false;
    if (filters.dateFrom && new Date(e.paymentDate) < new Date(filters.dateFrom)) return false;
    if (filters.dateTo) {
      const to = new Date(filters.dateTo);
      to.setHours(23, 59, 59, 999);
      if (new Date(e.paymentDate) > to) return false;
    }
    return true;
  });

  const getPartyChip = (type: string) => {
    if (type === 'account') return <Chip label="Account" size="small" style={{ fontSize: 11, height: '16px', backgroundColor: '#e0f2f1', color: '#00695c' }} />;
    if (type === 'employee') return <Chip label="Employee" size="small" style={{ fontSize: 11, height: '16px', backgroundColor: '#e3f2fd', color: '#1565c0' }} />;
    return <Chip label="Vendor" size="small" style={{ fontSize: 11, height: '16px', backgroundColor: '#edf6ef', color: '#49a563' }} />;
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'paymentDate',
      headerName: 'Date',
      flex: 1,
      renderCell: ({ row }: GridValueGetterParams) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.2' }}>
          <span style={{ fontWeight: 500 }}>{moment(row.paymentDate).format("DD MMM YYYY")}</span>
          <span style={{ color: '#6b7280', fontStyle: 'italic', fontSize: 12 }}>{moment(row.paymentDate).format("hh:mm A")}</span>
        </div>
      ),
    },
    {
      field: 'partyName',
      headerName: 'Party & Reference',
      flex: 1.5,
      renderCell: ({ row }: GridValueGetterParams) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.3' }}>
          <span style={{ fontWeight: 500 }}>{row.partyName}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
            {getPartyChip(row.partyType)}
            <Chip label={row.referenceId || (row.txnId ? `EXP-${String(row.txnId).padStart(3, '0')}` : '')} size="small" style={{ fontSize: 11, height: '16px', backgroundColor: '#f5f3ff', color: '#6366f1', fontWeight: 400 }} />
          </div>
        </div>
      ),
    },
    {
      field: 'category',
      headerName: 'Category',
      flex: 1,
      renderCell: ({ row }: GridValueGetterParams) => row.expenseCategory?.name || '-',
    },
    {
      field: 'amount',
      headerName: 'Amount',
      flex: 0.8,
      type: 'number',
      align: 'left',
      headerAlign: 'left',
      renderCell: ({ row }: GridValueGetterParams) => (
        <span style={{ fontWeight: 600 }}>{Utils.formatNumber(parseFloat(row.amount).toFixed(2))}</span>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      renderCell: () => (
        <Chip label="Approved" size="small" style={{ fontSize: 13, height: '24px', fontWeight: 600, backgroundColor: '#e3f2fd', color: '#1565c0', borderRadius: '4px' }} />
      ),
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 0.8,
      sortable: false,
      renderCell: ({ row }: GridValueGetterParams) => (
        <Button
          variant="contained"
          size="small"
          startIcon={<PaymentIcon />}
          onClick={() => {
            const partyExpenses = expenses.filter(e => e.partyId === row.partyId);
            setSettleExpenses(partyExpenses);
            setSettleParty({ _id: row.partyId, name: row.partyName, legalName: row.partyName, _type: row.partyType });
            setSettleDrawerOpen(true);
          }}
          sx={{ textTransform: 'none', fontSize: '0.75rem', height: 28, backgroundColor: '#059669', '&:hover': { backgroundColor: '#047857' } }}
        >
          Settle Now
        </Button>
      ),
    },
  ], [expenses]);

  return (
    <Box sx={{ width: '100%', pr: '10px' }}>
      {/* Filter Bar + Settle Now Button */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <ExpenseFilter
            onFiltersChange={handleFiltersChange}
            currentFilters={filters}
            categories={categories || []}
          />
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<PaymentIcon />}
          onClick={() => { setSettleExpenses(expenses); setSettleParty(null); setSettleDrawerOpen(true); }}
          sx={{ textTransform: 'none', fontSize: '0.8rem', height: 32, whiteSpace: 'nowrap', backgroundColor: '#059669', '&:hover': { backgroundColor: '#047857' } }}
        >
          Settle Now
        </Button>
      </Box>

      {!loading && rowCount === 0 ? (
        <Paper
          elevation={0}
          sx={{
            height: '70vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#fafafb',
            border: '2px dashed #e0e0e0',
            borderRadius: 2,
            p: 4
          }}
        >
          <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: '50%',
                backgroundColor: 'primary.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                mb: 3
              }}
            >
              <AccountBalanceIcon sx={{ fontSize: 60, color: 'primary.main' }} />
            </Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, color: '#333', mb: 1 }}
            >
              No Expenses to Settle
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: '#666', mb: 4, lineHeight: 1.6 }}
            >
              No approved expenses are pending settlement. Once expenses are approved, they will appear here for settlement.
            </Typography>
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 3, color: '#999' }}
            >
              Need help? Check our guide on expense settlement
            </Typography>
          </Box>
        </Paper>
      ) : (
        <div style={{ height: '77vh', width: '100%', overflow: 'auto' }}>
          <CustomDataGrid
            columns={columns}
            rows={filteredExpenses}
            rowCount={rowCount}
            rowsPerPageOptions={defaultRowsPerPageOptions}
            page={page}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            getRowId={(row: any) => row._id}
            loading={loading}
          />
        </div>
      )}

      {/* Settlement Form Drawer */}
      <Drawer disableEscapeKeyDown={false} anchor="right" open={settleDrawerOpen}>
        {settleDrawerOpen && (
          <DialogExpenseSettlementForm
            open={settleDrawerOpen}
            expenses={settleExpenses}
            partyName=""
            partyType=""
            preSelectedParty={settleParty}
            onClose={(settled) => { setSettleDrawerOpen(false); setSettleParty(null); if (settled) loadData(); }}
          />
        )}
      </Drawer>
    </Box>
  );
};

export default ExpenseSettlement;
